import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Paper, Select,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { bonusTrackerAPI } from '../../services/api';
import {
  BonusProgram, EmployeeOption, MILESTONE_LABEL, MilestoneState, REFERRAL_LABEL, ReferralDetail, ReferralInfo, ReferralStatus, employeeLabel, errText, money, todayYmd,
} from './bonusTypes';

interface Props {
  /** Active referral programs (they pay milestones). */
  programs: BonusProgram[];
  canEnter: boolean;
  onOpenBonus: (recordId: string) => void;
}

const stateColor = (s: MilestoneState): 'default' | 'warning' | 'success' | 'error' => (s === 'DUE' ? 'warning' : s === 'CLAIMED' ? 'success' : s === 'LOST' ? 'error' : 'default');
const statusColor = (s: ReferralStatus): 'default' | 'warning' | 'success' | 'error' => (s === 'OPEN' ? 'warning' : s === 'HIRED' ? 'success' : s === 'LEFT' ? 'error' : 'default');
const when = (v: string) => new Date(v.includes('T') ? v : `${v.replace(' ', 'T')}Z`).toLocaleString();

/** The next day (today or later) the program's week ends on, or the end of this month for a monthly program. */
function defaultPeriodEnd(frequency: string, weekday: number | null): string {
  const now = new Date();
  if (frequency === 'MONTHLY') return new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0)).toISOString().slice(0, 10);
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  while (d.getUTCDay() !== (weekday ?? 6)) d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

const useEmployees = () => {
  const [options, setOptions] = useState<EmployeeOption[]>([]);
  const search = useCallback(async (text: string) => {
    try { setOptions(await bonusTrackerAPI.employees(text, 'ALL', undefined, true)); } catch { /* typing still works */ }
  }, []);
  useEffect(() => { void search(''); }, [search]);
  return { options, search };
};

const ReferralsPanel: React.FC<Props> = ({ programs, canEnter, onOpenBonus }) => {
  const [rows, setRows] = useState<ReferralInfo[]>([]);
  const [status, setStatus] = useState('ALL');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setRows(await bonusTrackerAPI.referrals(status === 'ALL' ? undefined : status)); } catch (e) { setMessage({ type: 'error', text: errText(e) }); }
  }, [status]);
  useEffect(() => { void load(); }, [load]);

  return (
    <Box>
      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          An employee refers a candidate. After the hire, each milestone of the referral program falls due some days later, if the person is still employed.
          A due milestone is added to a bonus for the week, where it goes through the usual approvals. Nothing is added or paid automatically.
        </Typography>
        {canEnter && <Button variant="contained" sx={{ flexShrink: 0 }} onClick={() => setCreating(true)} disabled={!programs.length}>Register a referral</Button>}
      </Stack>
      {!programs.length && <Alert severity="info" sx={{ mb: 2 }}>There is no referral program yet. A super user creates one under Programs with the method "Referral milestones after the hire".</Alert>}
      <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
        <InputLabel>Status</InputLabel>
        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <MenuItem value="ALL">All</MenuItem>
          <MenuItem value="OPEN">Waiting for the hire</MenuItem>
          <MenuItem value="HIRED">Hired</MenuItem>
          <MenuItem value="LEFT">Left</MenuItem>
          <MenuItem value="CANCELLED">Cancelled</MenuItem>
        </Select>
      </FormControl>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Referral</TableCell>
              <TableCell>Referred by</TableCell>
              <TableCell>Candidate</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Hire date</TableCell>
              <TableCell>Milestones</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.code}<Typography variant="caption" display="block" color="text.secondary">{r.programName}</Typography></TableCell>
                <TableCell>{r.referrerName}{r.referrerNumber ? ` (${r.referrerNumber})` : ''}</TableCell>
                <TableCell>{r.candidateName}</TableCell>
                <TableCell><Chip size="small" color={statusColor(r.status)} label={REFERRAL_LABEL[r.status]} /></TableCell>
                <TableCell>{r.hireDate || '-'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {r.milestones.map((m) => <Chip key={m.number} size="small" variant="outlined" color={stateColor(m.state)} label={`${m.label}: ${MILESTONE_LABEL[m.state]}`} />)}
                  </Stack>
                </TableCell>
                <TableCell align="right"><Button size="small" onClick={() => setOpenId(r.id)}>Open</Button></TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={7}><Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No referrals yet.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      {creating && <NewReferral programs={programs} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); setMessage({ type: 'success', text: 'Referral registered.' }); void load(); }} />}
      {openId && <ReferralDetailDialog id={openId} canEnter={canEnter} onClose={() => setOpenId(null)} onChanged={(text) => { if (text) setMessage({ type: 'success', text }); void load(); }} onOpenBonus={(id) => { setOpenId(null); onOpenBonus(id); }} />}
    </Box>
  );
};

/* ------------------------------ register ------------------------------ */

const NewReferral: React.FC<{ programs: BonusProgram[]; onClose: () => void; onSaved: () => void }> = ({ programs, onClose, onSaved }) => {
  const { options, search } = useEmployees();
  const [programId, setProgramId] = useState(programs.length === 1 ? programs[0].id : '');
  const [referrer, setReferrer] = useState<EmployeeOption | null>(null);
  const [candidate, setCandidate] = useState('');
  const [hireDate, setHireDate] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await bonusTrackerAPI.createReferral({ programId, referrerId: referrer!.id, candidateName: candidate, hireDate: hireDate || undefined, note });
      onSaved();
    } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Register a referral</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <FormControl size="small" fullWidth required>
          <InputLabel>Referral program</InputLabel>
          <Select label="Referral program" value={programId} onChange={(e) => setProgramId(e.target.value)}>
            {programs.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Autocomplete size="small" options={options} value={referrer} getOptionLabel={employeeLabel} isOptionEqualToValue={(a, b) => a.id === b.id} filterOptions={(x) => x}
          onInputChange={(_e, v, reason) => { if (reason === 'input') void search(v); }} onChange={(_e, v) => setReferrer(v)}
          renderInput={(params) => <TextField {...params} label="Employee who referred" required />} />
        <TextField size="small" required label="Name of the person referred" value={candidate} onChange={(e) => setCandidate(e.target.value)} />
        <TextField size="small" type="date" label="Hire date (if already hired)" InputLabelProps={{ shrink: true }} value={hireDate} onChange={(e) => setHireDate(e.target.value)}
          helperText="Leave empty until the person starts. Milestones count their days from this date." inputProps={{ max: todayYmd() }} />
        <TextField size="small" label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !programId || !referrer || !candidate.trim()}>Register</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------ detail ------------------------------ */

const ReferralDetailDialog: React.FC<{ id: string; canEnter: boolean; onClose: () => void; onChanged: (text?: string) => void; onOpenBonus: (recordId: string) => void }> = ({ id, canEnter, onClose, onChanged, onOpenBonus }) => {
  const [r, setR] = useState<ReferralDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [hire, setHire] = useState('');
  const [leftDate, setLeftDate] = useState('');
  const [note, setNote] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  const load = useCallback(async () => {
    try {
      const d: ReferralDetail = await bonusTrackerAPI.referral(id);
      setR(d);
      setHire((h) => h || d.hireDate || '');
      setPeriodEnd((p) => p || defaultPeriodEnd(d.frequency, d.periodEndWeekday));
    } catch (e) { setError(errText(e)); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  const act = async (fn: () => Promise<ReferralDetail>, text: string) => {
    setBusy(true);
    setError(null);
    try { setR(await fn()); setNote(''); onChanged(text); } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };

  const claimed = Boolean(r?.milestones.some((m) => m.state === 'CLAIMED'));
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{r ? `${r.code} · ${r.candidateName}` : 'Referral'}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        {r && (
          <>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip color={statusColor(r.status)} label={REFERRAL_LABEL[r.status]} />
              <Typography variant="body2">Referred by {r.referrerName} · {r.programName}{r.ruleVersion ? ` · rule version ${r.ruleVersion}` : ''}{r.hireDate ? ` · hired ${r.hireDate}` : ''}{r.endDate ? ` · left ${r.endDate}` : ''}</Typography>
            </Stack>
            {r.note && <Typography variant="body2" color="text.secondary">{r.note}</Typography>}

            <Table size="small">
              <TableHead><TableRow><TableCell>Milestone</TableCell><TableCell>Due</TableCell><TableCell align="right">Pays</TableCell><TableCell>State</TableCell><TableCell align="right" /></TableRow></TableHead>
              <TableBody>
                {r.milestones.map((m) => (
                  <TableRow key={m.number}>
                    <TableCell>{m.label}{m.label !== `${m.days} days` && <Typography component="span" variant="caption" color="text.secondary"> ({m.days} days after the hire)</Typography>}</TableCell>
                    <TableCell>{m.dueDate || 'after the hire'}</TableCell>
                    <TableCell align="right">{money(m.amount, r.currency)}</TableCell>
                    <TableCell><Chip size="small" color={stateColor(m.state)} label={MILESTONE_LABEL[m.state]} /></TableCell>
                    <TableCell align="right">
                      {m.state === 'DUE' && canEnter && (
                        <Button size="small" variant="contained" disabled={busy || !periodEnd}
                          onClick={() => void act(() => bonusTrackerAPI.claimMilestone(id, m.number, periodEnd), `${m.label} added to the bonus ending ${periodEnd}. Follow its approvals in Bonuses.`)}>Add to bonus</Button>
                      )}
                      {m.state === 'CLAIMED' && m.claim && <Button size="small" onClick={() => onOpenBonus(m.claim!.recordId)}>Open bonus ({m.claim.periodEnd})</Button>}
                    </TableCell>
                  </TableRow>
                ))}
                {!r.milestones.length && <TableRow><TableCell colSpan={5}><Typography color="text.secondary">This program has no milestones.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
            {r.milestones.some((m) => m.state === 'DUE') && canEnter && (
              <TextField size="small" type="date" label="Add to the bonus ending" InputLabelProps={{ shrink: true }} value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
                helperText={r.frequency === 'WEEKLY' ? 'The last day of the program week (it must be that weekday).' : 'The last day of the month.'} sx={{ maxWidth: 260 }} />
            )}

            {canEnter && (r.status === 'OPEN' || r.status === 'HIRED') && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{r.status === 'OPEN' ? 'Record the hire' : 'Hire date'}</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField size="small" type="date" label="Hire date" InputLabelProps={{ shrink: true }} value={hire} onChange={(e) => setHire(e.target.value)} disabled={claimed} inputProps={{ max: todayYmd() }} />
                  <Button variant="outlined" disabled={busy || !hire || claimed || hire === r.hireDate} onClick={() => void act(() => bonusTrackerAPI.referralHired(id, hire), 'Hire date saved.')}>Save hire date</Button>
                  {claimed && <Typography variant="caption" color="text.secondary">A milestone is already in a bonus, so the date is locked.</Typography>}
                </Stack>
              </Paper>
            )}
            {canEnter && (r.status === 'OPEN' || r.status === 'HIRED') && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{r.status === 'OPEN' ? 'The person will not be hired' : 'The person left'}</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  {r.status === 'HIRED' && <TextField size="small" type="date" label="Last day worked" InputLabelProps={{ shrink: true }} value={leftDate} onChange={(e) => setLeftDate(e.target.value)} />}
                  <TextField size="small" fullWidth label="Note (required)" value={note} onChange={(e) => setNote(e.target.value)} />
                  {r.status === 'HIRED' ? (
                    <Button color="error" variant="outlined" disabled={busy || !leftDate || !note.trim()} onClick={() => void act(() => bonusTrackerAPI.referralLeft(id, leftDate, note), 'Recorded that the person left. Milestones after that day are lost.')}>Mark as left</Button>
                  ) : (
                    <Button color="error" variant="outlined" disabled={busy || !note.trim()} onClick={() => void act(() => bonusTrackerAPI.referralCancel(id, note), 'Referral cancelled.')}>Cancel referral</Button>
                  )}
                </Stack>
              </Paper>
            )}
            <Box>
              <Typography variant="subtitle2">History</Typography>
              {r.events.map((e, i) => (
                <Typography key={i} variant="caption" display="block" color="text.secondary">{when(e.createdAt)} · {e.actorName} · {e.action.replace(/_/g, ' ').toLowerCase()}{e.note ? ` · ${e.note}` : ''}</Typography>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

export default ReferralsPanel;
